import React, { useState } from 'react';
import './BookingsManagement.css';

/**
 * AddNewBooking Component - Form for creating a new booking
 * @param {Object} props - Component props
 * @param {Function} props.onSubmit - Function to handle form submission
 * @param {Array} props.vehicleTypes - Array of available vehicle types
 * @param {Array} props.services - Array of available services
 * @param {Function} props.onCancel - Function to handle form cancellation
 * @returns {JSX.Element} - Rendered component
 */
function AddNewBooking({ onSubmit, vehicleTypes, services, onCancel }) {
  const [selectedVehicleType, setSelectedVehicleType] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  const handleServiceChange = (service) => {
    setSelectedService(service);
    setSelectedAddOns([]);
  };

  const handleAddOnToggle = (addon) => {
    setSelectedAddOns(prev => {
      const exists = prev.find(item => item.id === addon.id);
      if (exists) {
        return prev.filter(item => item.id !== addon.id);
      } else {
        return [...prev, addon];
      }
    });
  };

  const handleVehicleTypeChange = (vehicleType) => {
    setSelectedVehicleType(vehicleType);
    setSelectedService(null);
    setSelectedAddOns([]);
  };

  const calculateTotalPrice = () => {
    if (!selectedVehicleType || !selectedService) return 0;

    const basePrice = selectedService.services_base_price?.find(
      price => price.vehicle_types?.id === selectedVehicleType.id
    )?.price || 0;

    const addonsTotal = selectedAddOns.reduce((sum, addon) => {
      const addonPrice = typeof addon.price === 'number' ? addon.price : 0;
      return sum + addonPrice;
    }, 0);

    return basePrice + addonsTotal;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const bookingDetails = {
      vehicleTypeId: selectedVehicleType.id,
      serviceId: selectedService.id,
      addons: selectedAddOns.map(addon => addon.id),
      date: bookingData.date,
      time: bookingData.time,
      totalPrice: calculateTotalPrice(),
      firstName: bookingData.firstName,
      lastName: bookingData.lastName,
      email: bookingData.email,
      phone: bookingData.phone
    };

    onSubmit(bookingDetails);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="bookings-management__form">
      <div className="bookings-management__form-section">
        <h3 className="bookings-management__form-subtitle">Customer Information</h3>
        <div className="bookings-management__form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              required
              value={bookingData.firstName}
              onChange={handleInputChange}
              placeholder="Enter first name"
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              required
              value={bookingData.lastName}
              onChange={handleInputChange}
              placeholder="Enter last name"
            />
          </div>
        </div>
        <div className="bookings-management__form-row">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={bookingData.email}
              onChange={handleInputChange}
              placeholder="Enter email address"
            />
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={bookingData.phone}
              onChange={handleInputChange}
              placeholder="Enter phone number"
            />
          </div>
        </div>
      </div>

      <div className="bookings-management__form-section">
        <h3 className="bookings-management__form-subtitle">Select Vehicle Type</h3>
        <div className="bookings-management__vehicle-options">
          {vehicleTypes.map(vehicle => (
            <div key={vehicle.id} className="bookings-management__vehicle-option">
              <label className={`vehicle-card ${selectedVehicleType?.id === vehicle.id ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="vehicleType"
                  checked={selectedVehicleType?.id === vehicle.id}
                  onChange={() => handleVehicleTypeChange(vehicle)}
                />
                <img src={vehicle.image_url} alt={vehicle.title} />
                <span className="vehicle-title">{vehicle.title}</span>
                <span className="vehicle-price">From ${vehicle.defaultPrice}</span>
              </label>
            </div>
          ))}
        </div>
      </div>

      {selectedVehicleType && (
        <div className="bookings-management__form-section">
          <h3 className="bookings-management__form-subtitle">Select Service</h3>
          <div className="bookings-management__service-options">
            {services.map(service => (
              <div key={service.id} className="bookings-management__service-option">
                <label className={`service-card ${selectedService?.id === service.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="service"
                    checked={selectedService?.id === service.id}
                    onChange={() => handleServiceChange(service)}
                  />
                  <span className="service-name">{service.name}</span>
                  {service.note && <span className="service-note">{service.note}</span>}
                </label>

                {selectedService?.id === service.id && service.service_addons?.length > 0 && (
                  <div className="bookings-management__addons">
                    <h4>Add-Ons:</h4>
                    <div className="addon-options">
                      {service.service_addons.map(addon => (
                        <label key={addon.id} className="addon-option">
                          <input
                            type="checkbox"
                            checked={selectedAddOns.some(item => item.id === addon.id)}
                            onChange={() => handleAddOnToggle(addon)}
                          />
                          <span className="addon-name">{addon.name}</span>
                          <span className="addon-price">${addon.price}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bookings-management__form-row">
        <input
          type="date"
          name="date"
          required
          value={bookingData.date}
          onChange={handleInputChange}
        />
        <select
          name="time"
          required
          value={bookingData.time}
          onChange={handleInputChange}
          className="time-select"
        >
          <option value="">Select a time</option>
          <option value="9:00 AM">9:00 AM</option>
          <option value="9:30 AM">9:30 AM</option>
          <option value="10:00 AM">10:00 AM</option>
          <option value="10:30 AM">10:30 AM</option>
          <option value="11:00 AM">11:00 AM</option>
          <option value="11:30 AM">11:30 AM</option>
          <option value="12:00 PM">12:00 PM</option>
          <option value="12:30 PM">12:30 PM</option>
          <option value="1:00 PM">1:00 PM</option>
          <option value="1:30 PM">1:30 PM</option>
          <option value="2:00 PM">2:00 PM</option>
          <option value="2:30 PM">2:30 PM</option>
          <option value="3:00 PM">3:00 PM</option>
          <option value="3:30 PM">3:30 PM</option>
          <option value="4:00 PM">4:00 PM</option>
          <option value="4:30 PM">4:30 PM</option>
          <option value="5:00 PM">5:00 PM</option>
        </select>
      </div>

      <div className="bookings-management__form-actions">
        <div className="total-price">
          Total Price: ${calculateTotalPrice()}
        </div>
        <button
          type="submit"
          className="bookings-management__submit-btn"
          disabled={!selectedVehicleType || !selectedService}
        >
          Create Booking
        </button>
        <button
          type="button"
          className="bookings-management__cancel-btn"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default AddNewBooking; 